/**
 * Slugs d'URL pour les pages générées (fiches pays, pages thématiques).
 *
 * Distinct de `StringUtils.sanitize()`, qui sert au RAPPROCHEMENT de saisies :
 * `sanitize()` supprime tout séparateur (« Corée du Sud » → « coreedusud »),
 * ce qui est exactement ce qu'il faut pour comparer une réponse tapée, et
 * exactement ce qu'il ne faut pas dans une URL.
 *
 * Module sans dépendance DOM ni Node : importable par le build et par le
 * runtime.
 */

/**
 * « Côte d'Ivoire » → « cote-divoire », « États-Unis » → « etats-unis ».
 *
 * L'apostrophe disparaît sans laisser de séparateur (convention la plus
 * répandue : « d'Ivoire » → « divoire »), les autres caractères non
 * alphanumériques deviennent un tiret unique.
 */
/**
 * Segment de chemin des fiches pays, traduit lui aussi : `/pays/france` face à
 * `/en/countries/france`. Un segment français dans une URL anglaise est un
 * signal contradictoire sur une page dont c'est justement l'atout.
 */
export const COUNTRY_SEGMENT = { fr: 'pays', en: 'countries' };

/**
 * Chemin d'une fiche pays, SANS le préfixe de langue (que `pageUrl` ajoute) :
 * `countryPath('fr', 'coree-du-sud')` → `pays/coree-du-sud`.
 */
export function countryPath(lang, slug) {
	return `${COUNTRY_SEGMENT[lang] || COUNTRY_SEGMENT.en}/${slug}`;
}

/**
 * Pages génériques à un seul template (pas de fiche par entité) dont le slug
 * diffère entre le français — la clé, celui du fichier dans `app/pages/` — et
 * l'anglais. Absente de cette table = même slug dans les deux langues (cas
 * par défaut : `/scores`, `/training`, `/faq`…).
 */
export const PAGE_SLUG_EN = {
	'a-propos': 'about',
	'mentions-legales': 'legal-notice',
	'parties-en-cours': 'games-in-progress',
	'mot-de-passe-oublie': 'forgot-password',
	connexion: 'login',
	inscription: 'register',
	profil: 'profile',
	desinscription: 'unsubscribe',
	passeport: 'passport',
	revision: 'review',
	// L'annuaire des pays est la racine des fiches pays (`/pays` liste,
	// `/pays/<pays>` détail) : même segment que `COUNTRY_SEGMENT`, pour que la
	// hiérarchie parent/enfant soit identique dans les deux langues.
	pays: COUNTRY_SEGMENT.en,
};

const PAGE_SLUG_FR = Object.fromEntries(Object.entries(PAGE_SLUG_EN).map(([fr, en]) => [en, fr]));

/** Slug anglais d'une page à partir de son slug source (français). */
export function toEnPageSlug(frSlug) {
	return PAGE_SLUG_EN[frSlug] || frSlug;
}

/** Slug français canonique d'une page à partir d'un slug FR ou EN. */
export function toFrPageSlug(slug) {
	return PAGE_SLUG_FR[slug] || slug;
}

/**
 * Traduit le slug d'un chemin à UN SEUL segment (`/a-propos` → `/about`,
 * `/pays/` → `/countries/` — le slash final éventuel est conservé tel quel).
 * Un chemin à plusieurs segments (fiches pays, ex. `/pays/coree-du-sud`) est
 * renvoyé inchangé : leur table de correspondance par pays n'est pas
 * embarquée côté client, seule cette poignée de pages génériques l'est.
 */
export function translatePagePath(path, translate) {
	const match = path.match(/^\/([^/]+)(\/)?$/);
	return match ? `/${translate(match[1])}${match[2] || ''}` : path;
}

export function slugify(name) {
	return String(name)
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '') // diacritiques
		.replace(/['’]/g, '')              // apostrophes : pas de séparateur
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
}
